import { ChevronRight, PanelRightClose, PanelRightOpen, RotateCw } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { ResizeHandle } from "@/components/ui/ResizeHandle";
import { useResizableColumn } from "@/components/ui/useResizableColumn";
import { ROUTES as RAW_FILES_VIEWER_ROUTES } from "@/raw-files-viewer/navigation";
import { ProcessedDocumentTabs } from "@/raw-files-viewer/ProcessedDocumentTabs";
import { useProcessedDocumentsForEncounter } from "@/raw-files-viewer/useProcessedDocumentsForEncounter";
import { CorrectionPageImage } from "./CorrectionPageImage";
import { CorrectionRow } from "./CorrectionRow";
import { groupByPage, isAnswered, rowLabel } from "./corrections";
import { JudgeReading } from "./JudgeReading";
import { ROUTES } from "./navigation";
import {
  useAnswerCorrection,
  useEncounterFormReadCorrections,
  useNextUnansweredEncounterId,
  usePendingAnswerIds,
} from "./useFormReadCorrections";

const WIDE_SCREEN = "(min-width: 1600px)";
const STICKY_PANE = "sticky top-4 max-h-[calc(100vh-7rem)] self-start overflow-auto";

export function FormReadCorrectionsEncounterPage() {
  const { encounterId = "" } = useParams<{ encounterId: string }>();
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(() => window.matchMedia(WIDE_SCREEN).matches);
  const { data: rows = [], isLoading, error } = useEncounterFormReadCorrections(encounterId);
  const saveAnswer = useAnswerCorrection(encounterId);
  const pendingIds = usePendingAnswerIds();
  const {
    data: processedDocuments,
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useProcessedDocumentsForEncounter(summaryOpen ? encounterId : null);
  const { data: nextEncounterId } = useNextUnansweredEncounterId(encounterId);
  const summaryColumn = useResizableColumn({
    initial: 28,
    min: 20,
    max: 45,
    storageKey: "form-read-corrections-summary-column-width",
    unit: "percent",
    invert: true,
  });

  const pages = groupByPage(rows);
  const activeRow = rows.find((row) => row.id === activeRowId) ?? pages[0]?.rows[0];
  const activePage = pages.find((page) => page.rows.some((row) => row.id === activeRow?.id));
  const summaries = (processedDocuments?.documents ?? []).filter((doc) => doc.fileIdentifier === "codeable_summary");
  const failedCount = processedDocuments?.failedCount ?? 0;
  const answeredCount = rows.filter(isAnswered).length;
  const summaryFailed = !!summaryError || failedCount > 0;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b pb-3">
        <Link to={ROUTES.formReadCorrections.route} className="text-sm text-muted-foreground hover:underline">
          ← All encounters
        </Link>
        <h1 className="text-base font-semibold">
          Encounter <span className="font-mono">{encounterId}</span>
        </h1>
        <Button asChild variant="outline" size="sm">
          <Link to={`${RAW_FILES_VIEWER_ROUTES.rawFilesViewer.route}/${encounterId}`}>Open in raw files viewer</Link>
        </Button>
        <div className="ml-auto flex items-center gap-3 text-sm">
          {rows.length > 0 && (
            <span className="text-muted-foreground">
              {answeredCount} of {rows.length} rows answered
            </span>
          )}
          {nextEncounterId && (
            <Button asChild variant={answeredCount === rows.length ? "default" : "outline"} size="sm">
              <Link to={`${ROUTES.formReadCorrections.route}/${nextEncounterId}`}>
                Next encounter
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          )}
        </div>
      </div>

      {saveAnswer.error && (
        <div className="text-destructive">Could not save the answer: {saveAnswer.error.message}</div>
      )}

      <div className="flex items-start gap-6">
        <div className="w-[380px] shrink-0 space-y-6">
          {isLoading && <div className="text-sm text-muted-foreground">Loading corrections…</div>}
          {error && <div className="text-destructive">{error.message}</div>}
          {!isLoading && !error && pages.length === 0 && (
            <div className="text-sm text-muted-foreground">No form-read corrections for this encounter.</div>
          )}
          {pages.map((page, index) => (
            <section key={page.key} className="space-y-3">
              {pages.length > 1 && (
                <h2 className="text-sm font-semibold">
                  Page {index + 1} of {pages.length}
                </h2>
              )}
              {page.rows.map((row) => (
                <CorrectionRow
                  key={row.id}
                  row={row}
                  label={rowLabel(row, page.rows)}
                  active={row.id === activeRow?.id}
                  saving={pendingIds.has(row.id)}
                  onSelect={() => setActiveRowId(row.id)}
                  onAnswer={(answer) => saveAnswer.mutate({ row, answer })}
                />
              ))}
            </section>
          ))}
        </div>

        <div className={`${STICKY_PANE} min-w-[200px] flex-1 rounded-md border`}>
          {activePage ? (
            <CorrectionPageImage rawBundleId={activePage.rawBundleId} pageKey={activePage.pageKey} />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">No page to show.</div>
          )}
          {activeRow && <JudgeReading row={activeRow} />}
        </div>

        {summaryOpen ? (
          <>
            <ResizeHandle
              onMouseDown={summaryColumn.handleMouseDown}
              isDragging={summaryColumn.isDragging}
              ariaLabel="Resize summary column"
            />
            <div style={{ width: `${summaryColumn.width}%` }} className={`${STICKY_PANE} min-w-[250px] shrink-0`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">Current summary</h2>
                <div className="flex items-center gap-1">
                  {summaryFailed && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => refetchSummary()}>
                      <RotateCw className="mr-1 h-4 w-4" aria-hidden="true" />
                      Retry
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSummaryOpen(false)}>
                    <PanelRightClose className="mr-1 h-4 w-4" aria-hidden="true" />
                    Hide
                  </Button>
                </div>
              </div>
              <ProcessedDocumentTabs
                key={encounterId}
                documents={summaries}
                isLoading={isLoadingSummary}
                error={summaryError}
                failedCount={failedCount}
              />
            </div>
          </>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setSummaryOpen(true)}>
            <PanelRightOpen className="mr-1 h-4 w-4" aria-hidden="true" />
            Show summary
          </Button>
        )}
      </div>
    </div>
  );
}
